import { useCallback, useEffect, useRef, useState } from 'react'

import { FormationStatus, type FormationStatusType } from '@/features/formation/domain'
import { productData, ProductDataError } from '@/lib/api/product-data-client'
import { registerBrowserSessionCleanup } from '@/lib/auth/browser-session-boundary'
import { reportMessage } from '@/spa/sentry'

interface FormationOrderData {
  id: string
  status: string
  display_status: FormationStatusType
  company_id?: string
  company_name: string
  company_name_with_designator: string
  formation_state: string
  package_type?: string
  created_at: string
  ein_status?: string
  company?: {
    company_type?: string
    formation_state?: string
  }
}

interface UseFormationOrderReturn {
  order: FormationOrderData | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  isPolling: boolean
  isInitialLoad: boolean // True only on first load with no cache
  isRevalidating: boolean // True when cache exists but fetching fresh
}

interface UseFormationOrderOptions {
  enabled?: boolean
}

// Cache configuration
const CACHE_DURATION = 30000 // 30 seconds cache validity
const CACHE_KEY = 'formation_order_cache'
const ERROR_ROUTE_PATHS = new Set(['/404', '/500'])

interface CacheData {
  order: FormationOrderData | null
  noOrder: boolean
  timestamp: number
}

function isErrorRoutePathname(pathname: string): boolean {
  return ERROR_ROUTE_PATHS.has(pathname)
}

function isTransientFormationFetchError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true
  }

  // `productData()` wraps every fetch-never-reached-the-Worker failure (offline,
  // DNS, blocked) into a uniform `ProductDataError` with code `NETWORK`, moving
  // the original message (e.g. "Load failed") into `.cause` instead of
  // `.message`. The code is the direct signal now — no need to string-match.
  if (error instanceof ProductDataError && error.code === 'NETWORK') {
    return true
  }

  if (!error || typeof error !== 'object') {
    return false
  }

  const message = 'message' in error && typeof error.message === 'string' ? error.message.toLowerCase() : ''

  if (!message) {
    return false
  }

  return (
    message.includes('load failed') ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('aborted')
  )
}

/**
 * Get cached data from sessionStorage (survives page refreshes)
 */
function getCachedData(): CacheData | null {
  if (typeof window === 'undefined') return null

  try {
    const cached = sessionStorage.getItem(CACHE_KEY)
    if (!cached) return null
    return JSON.parse(cached) as CacheData
  } catch {
    return null
  }
}

/**
 * Save data to sessionStorage cache
 */
function setCachedData(data: CacheData): void {
  if (typeof window === 'undefined') return

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data))
  } catch {
    // Silently fail if storage is full or unavailable
  }
}

/**
 * Check if cache is still valid
 */
function isCacheValid(): boolean {
  const cached = getCachedData()
  if (!cached) return false
  return Date.now() - cached.timestamp < CACHE_DURATION
}

/**
 * Get cached order if valid
 */
function getCachedOrder(): FormationOrderData | null {
  const cached = getCachedData()
  if (!cached || !isCacheValid()) return null
  return cached.order
}

/**
 * Check if "no order" state is cached
 */
function isCachedNoOrder(): boolean {
  const cached = getCachedData()
  if (!cached || !isCacheValid()) return false
  return cached.noOrder
}

/**
 * Clear the formation order cache (useful after payment completion)
 */
export function clearFormationOrderCache(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(CACHE_KEY)
}

// The cache holds this customer's order; sign-out is an SPA navigation, so the
// next customer in the tab would otherwise read it within its 30 s life.
registerBrowserSessionCleanup(clearFormationOrderCache)

/**
 * Hook to fetch the current user's formation order
 * Returns the most recent active formation order (not completed or cancelled)
 * Includes polling mechanism to wait for webhook processing after payment
 *
 * UX improvements:
 * - Uses 30-second memory cache to prevent loading flicker on refresh
 * - Distinguishes between initial load (no cache) and revalidation (has cache)
 * - Returns cached data immediately while fetching fresh data in background
 */
export function useFormationOrder(options: UseFormationOrderOptions = {}): UseFormationOrderReturn {
  const enabled = options.enabled ?? true

  // Initialize state from cache if valid (prevents loading flicker)
  const [order, setOrder] = useState<FormationOrderData | null>(() => {
    return enabled ? getCachedOrder() : null
  })

  // Only show loading state if we have no cached data AND haven't determined "no order"
  const [loading, setLoading] = useState(() => {
    if (!enabled) {
      return false
    }

    // If we have valid cache (either order data OR "no order" state), don't show loading
    // This prevents showing skeleton for brand new users on subsequent page loads
    if (isCacheValid() || isCachedNoOrder()) {
      return false
    }
    return true
  })

  const [error, setError] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [isRevalidating, setIsRevalidating] = useState(false)

  // Track if this is truly the initial load (no cache existed at mount)
  // Don't consider it "initial load" if we've already cached "no order"
  const [isInitialLoad, setIsInitialLoad] = useState(() => {
    if (!enabled) {
      return false
    }
    return !isCacheValid() && !isCachedNoOrder()
  })

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const fetchAbortControllerRef = useRef<AbortController | null>(null)
  const attemptCountRef = useRef(0)
  const hasFetchedRef = useRef(false) // Guard to prevent multiple initial fetches
  const isMountedRef = useRef(true) // UI-01: Track mount state to prevent memory leaks
  const maxPollingAttempts = 10 // 10 attempts × 3 seconds = 30 seconds max

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    setIsPolling(false)
    attemptCountRef.current = 0
  }, [])

  // The polling interval below needs to call the *current* fetchFormationOrder
  // from inside its own body, which is a self-reference to the `const` being
  // initialized — referencing that identifier by name there is a stale-closure
  // risk (and one React Compiler statically rejects). A ref sidesteps both:
  // the interval always calls the latest version via fetchFormationOrderRef.current.
  const fetchFormationOrderRef = useRef<(isPollingAttempt?: boolean) => Promise<void>>(async () => {})

  const fetchFormationOrder = useCallback(
    async (isPollingAttempt = false) => {
      if (!enabled) return

      // UI-01: Early return if component unmounted
      if (!isMountedRef.current) return

      try {
        const pathname = typeof window !== 'undefined' ? window.location.pathname : ''

        if (pathname && isErrorRoutePathname(pathname)) {
          if (!isPollingAttempt) {
            setLoading(false)
            setIsRevalidating(false)
            setIsInitialLoad(false)
          }
          return
        }

        if (!isPollingAttempt) {
          // If we have cached data, mark as revalidating (not loading)
          if (isCacheValid()) {
            setIsRevalidating(true)
            // Don't set loading=true, we have data to show
          } else {
            setLoading(true)
          }
          setError(null)
        }

        if (fetchAbortControllerRef.current) {
          fetchAbortControllerRef.current.abort()
        }

        const abortController = new AbortController()
        fetchAbortControllerRef.current = abortController

        const data = await productData<{ formationOrder: FormationOrderData | null }>('/api/customer/formation-order', {
          signal: abortController.signal,
        })

        if (fetchAbortControllerRef.current === abortController) {
          fetchAbortControllerRef.current = null
        }

        const formationData = data.formationOrder

        // UI-01: Check mount state before updating state after async operation
        if (!isMountedRef.current) return

        // Handle case where no formation order exists yet (company/order may not be created yet due to webhook processing)
        if (!formationData) {
          // If this is the first attempt and we're expecting an order, start polling
          if (!isPollingAttempt && attemptCountRef.current === 0) {
            // Check if we should poll (e.g., user just completed payment)
            const urlParams = new URLSearchParams(window.location.search)
            const shouldPoll = urlParams.has('payment_intent') || urlParams.has('session_id')

            if (shouldPoll) {
              // Clear cache when polling after payment (need fresh data)
              clearFormationOrderCache()
              setIsPolling(true)
              setIsInitialLoad(false) // No longer initial, now polling
              attemptCountRef.current = 1

              // Start polling every 3 seconds
              pollingIntervalRef.current = setInterval(async () => {
                // UI-02: Check mount state in interval callback
                if (!isMountedRef.current) {
                  stopPolling()
                  return
                }

                attemptCountRef.current += 1

                if (attemptCountRef.current >= maxPollingAttempts) {
                  // The one polling outcome worth a report: a customer back from
                  // checkout whose order never appeared. A fixed message (no ids)
                  // so every occurrence groups into one issue. The per-attempt
                  // and "found" lines were console.warn, which
                  // captureConsoleIntegration forwarded to Sentry as a new issue
                  // per order (NONRESIDENT-EG).
                  reportMessage('Formation order still missing after post-checkout polling', {
                    level: 'warning',
                    tags: { component: 'formation-order', operation: 'post_checkout_poll' },
                  })
                  stopPolling()
                  if (isMountedRef.current) {
                    setError('Order creation is taking longer than expected. Please refresh the page.')
                    setLoading(false)
                    setIsRevalidating(false)
                  }
                  return
                }

                await fetchFormationOrderRef.current(true)
              }, 3000)
            }
          }

          if (!isPollingAttempt) {
            // Cache the "no order" state to prevent loading on next refresh
            setCachedData({
              order: null,
              noOrder: true,
              timestamp: Date.now(),
            })

            // Batch all state updates together to prevent showing skeleton
            // between state update renders
            setOrder(null)
            setLoading(false)
            setIsRevalidating(false)
            setIsInitialLoad(false)

            // CRITICAL: Immediately update display states to prevent skeleton flash
            // This ensures the dashboard condition (!isInitialLoad && !loading)
            // evaluates correctly before the next render
          }
          return
        }

        if (formationData && formationData.display_status !== (FormationStatus.CANCELLED as FormationStatusType)) {
          const orderData: FormationOrderData = {
            id: formationData.id,
            status: formationData.status,
            display_status: formationData.display_status,
            company_id: formationData.company_id,
            company_name: formationData.company_name,
            company_name_with_designator: formationData.company_name_with_designator,
            formation_state: formationData.formation_state || formationData.company?.formation_state || 'Delaware',
            package_type: formationData.package_type,
            created_at: formationData.created_at,
            ein_status: formationData.ein_status,
            company: formationData.company,
          }

          // Update cache
          setCachedData({
            order: orderData,
            noOrder: false,
            timestamp: Date.now(),
          })

          setOrder(orderData)

          // Stop polling if order found
          if (isPollingAttempt) {
            stopPolling()
          }
          setLoading(false)
          setIsRevalidating(false)
          setIsInitialLoad(false)
          setError(null)
        } else {
          // Cache the "no active order" state
          setCachedData({
            order: null,
            noOrder: true,
            timestamp: Date.now(),
          })

          setOrder(null)
          if (!isPollingAttempt) {
            setLoading(false)
            setIsRevalidating(false)
            setIsInitialLoad(false)
          }
        }
      } catch (err) {
        fetchAbortControllerRef.current = null

        if (isTransientFormationFetchError(err)) {
          if (!isMountedRef.current) return

          const cachedOrderData = getCachedOrder()
          if (cachedOrderData) {
            setOrder(cachedOrderData)
          }

          setLoading(false)
          setIsRevalidating(false)
          setIsInitialLoad(false)
          return
        }

        // UI-01: Check mount state before updating state in error handler
        if (!isMountedRef.current) return

        console.error('Error fetching formation order:', err)
        // `productData()` throws `HTTP ${status}` for a non-2xx response with no
        // structured error body — translate the two statuses this hook has
        // always distinguished back to their prior user-facing text.
        const message =
          err instanceof ProductDataError && err.status === 401
            ? 'Unauthorized'
            : err instanceof ProductDataError && err.status === 500
              ? 'Server error'
              : err instanceof Error
                ? err.message
                : 'Failed to fetch formation order'
        setError(message)
        setLoading(false)
        setIsRevalidating(false)
        setIsInitialLoad(false)
        stopPolling()

        // On error, if we have cached data, keep showing it
        const cachedOrderData = getCachedOrder()
        if (cachedOrderData) {
          setOrder(cachedOrderData)
        }
      }
    },
    [enabled, stopPolling],
  )

  useEffect(() => {
    fetchFormationOrderRef.current = fetchFormationOrder
  })

  useEffect(() => {
    if (enabled) {
      return
    }

    hasFetchedRef.current = false
    attemptCountRef.current = 0
    setOrder(null)
    setError(null)
    setLoading(false)
    setIsRevalidating(false)
    setIsInitialLoad(false)

    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort()
      fetchAbortControllerRef.current = null
    }

    stopPolling()
  }, [enabled, stopPolling])

  useEffect(() => {
    // This effect re-runs when `enabled` flips; its previous cleanup marked the
    // hook unmounted, which would make every later fetch return early.
    isMountedRef.current = true
    // A disabled hook fetches nothing, so it must not use up the initial fetch.
    if (!enabled) return
    // Guard to prevent multiple fetches if effect runs multiple times
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true

    fetchFormationOrder()

    // UI-01: Cleanup on unmount - prevent memory leaks
    return () => {
      isMountedRef.current = false
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort()
        fetchAbortControllerRef.current = null
      }
      stopPolling()
    }
  }, [enabled, fetchFormationOrder, stopPolling])

  return {
    order,
    loading,
    error,
    refetch: fetchFormationOrder,
    isPolling,
    isInitialLoad,
    isRevalidating,
  }
}
